import { useState } from "react";
import { ServerFnError } from "catmint/error";
import {
  getServerTime,
  greetUser,
  throwClientSafeError,
  returnClientSafeError,
  throwUnsafeError,
} from "./actions.fn";

export function ServerFnClient() {
  const [timeResult, setTimeResult] = useState<{
    time: string;
    pid: number;
    nodeVersion: string;
  } | null>(null);
  const [greetResult, setGreetResult] = useState<{
    message: string;
    timestamp: string;
  } | null>(null);
  const [name, setName] = useState("");
  const [loadingTime, setLoadingTime] = useState(false);
  const [loadingGreet, setLoadingGreet] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Error demo state
  const [errorResult, setErrorResult] = useState<{
    label: string;
    thrown: boolean;
    message: string;
    statusCode?: number;
    data?: Record<string, unknown>;
  } | null>(null);
  const [loadingError, setLoadingError] = useState<string | null>(null);

  async function handleGetTime() {
    setLoadingTime(true);
    setError(null);
    try {
      const result = await getServerTime();
      setTimeResult(result);
    } catch (err) {
      setError(
        err instanceof Error ? err.message : "Failed to call server function",
      );
    } finally {
      setLoadingTime(false);
    }
  }

  async function handleGreet() {
    if (!name.trim()) return;
    setLoadingGreet(true);
    setError(null);
    try {
      const result = await greetUser({ name: name.trim() });
      setGreetResult(result);
    } catch (err) {
      setError(
        err instanceof Error ? err.message : "Failed to call server function",
      );
    } finally {
      setLoadingGreet(false);
    }
  }

  async function handleThrowClientSafe() {
    setLoadingError("throwClientSafe");
    setErrorResult(null);
    try {
      await throwClientSafeError();
      setErrorResult({
        label: "throwClientSafeError",
        thrown: false,
        message: "No error — this shouldn't happen",
      });
    } catch (err) {
      if (err instanceof ServerFnError) {
        setErrorResult({
          label: "throwClientSafeError",
          thrown: true,
          message: err.message,
          statusCode: err.statusCode,
          data: err.data,
        });
      } else {
        setErrorResult({
          label: "throwClientSafeError",
          thrown: true,
          message: err instanceof Error ? err.message : String(err),
        });
      }
    } finally {
      setLoadingError(null);
    }
  }

  async function handleReturnClientSafe() {
    setLoadingError("returnClientSafe");
    setErrorResult(null);
    try {
      const result = await returnClientSafeError();
      if (result instanceof ServerFnError) {
        setErrorResult({
          label: "returnClientSafeError",
          thrown: false,
          message: result.message,
          statusCode: result.statusCode,
          data: result.data,
        });
      } else {
        setErrorResult({
          label: "returnClientSafeError",
          thrown: false,
          message: JSON.stringify(result),
        });
      }
    } catch (err) {
      setErrorResult({
        label: "returnClientSafeError",
        thrown: true,
        message: err instanceof Error ? err.message : String(err),
      });
    } finally {
      setLoadingError(null);
    }
  }

  async function handleThrowUnsafe() {
    setLoadingError("throwUnsafe");
    setErrorResult(null);
    try {
      await throwUnsafeError();
      setErrorResult({
        label: "throwUnsafeError",
        thrown: false,
        message: "No error — this shouldn't happen",
      });
    } catch (err) {
      if (err instanceof ServerFnError) {
        setErrorResult({
          label: "throwUnsafeError",
          thrown: true,
          message: err.message,
          statusCode: err.statusCode,
          data: err.data,
        });
      } else {
        setErrorResult({
          label: "throwUnsafeError",
          thrown: true,
          message: err instanceof Error ? err.message : String(err),
        });
      }
    } finally {
      setLoadingError(null);
    }
  }

  return (
    <div className="mt-4 space-y-6">
      {error && (
        <div className="p-3 bg-red-50 border border-red-200 rounded text-red-700 text-sm">
          {error}
        </div>
      )}

      {/* getServerTime */}
      <div className="p-4 bg-amber-50 border border-amber-200 rounded">
        <h3 className="font-semibold text-sm mb-2">
          getServerTime() — via RPC
        </h3>
        <button
          type="button"
          onClick={handleGetTime}
          disabled={loadingTime}
          className="px-4 py-2 rounded bg-amber-500 hover:bg-amber-600 disabled:opacity-50 text-white font-medium transition-colors"
        >
          {loadingTime ? "Calling..." : "Call getServerTime()"}
        </button>
        {timeResult && (
          <div className="mt-3 p-3 bg-white rounded border font-mono text-sm">
            <div>time: {timeResult.time}</div>
            <div>pid: {timeResult.pid}</div>
            <div>node: {timeResult.nodeVersion}</div>
          </div>
        )}
      </div>

      {/* greetUser */}
      <div className="p-4 bg-amber-50 border border-amber-200 rounded">
        <h3 className="font-semibold text-sm mb-2">
          greetUser(&#123; name &#125;) — via RPC
        </h3>
        <div className="flex gap-2">
          <input
            type="text"
            value={name}
            onChange={(e) => setName(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && handleGreet()}
            placeholder="Enter your name"
            className="flex-1 px-3 py-2 border rounded focus:outline-none focus:ring-2 focus:ring-amber-200"
          />
          <button
            type="button"
            onClick={handleGreet}
            disabled={loadingGreet || !name.trim()}
            className="px-4 py-2 rounded bg-amber-500 hover:bg-amber-600 disabled:opacity-50 text-white font-medium transition-colors"
          >
            {loadingGreet ? "Calling..." : "Call greetUser()"}
          </button>
        </div>
        {greetResult && (
          <div className="mt-3 p-3 bg-white rounded border text-sm">
            <div>{greetResult.message}</div>
            <div className="text-gray-400 text-xs mt-1">
              at {greetResult.timestamp}
            </div>
          </div>
        )}
      </div>

      {/* Error handling demos */}
      <div className="p-4 bg-red-50 border border-red-200 rounded">
        <h3 className="font-semibold text-sm mb-3">Error Handling Demos</h3>
        <div className="flex flex-wrap gap-2">
          <button
            type="button"
            onClick={handleThrowClientSafe}
            disabled={loadingError !== null}
            className="px-4 py-2 rounded bg-red-500 hover:bg-red-600 disabled:opacity-50 text-white font-medium transition-colors text-sm"
          >
            {loadingError === "throwClientSafe"
              ? "Calling..."
              : "Throw ClientSafeError"}
          </button>
          <button
            type="button"
            onClick={handleReturnClientSafe}
            disabled={loadingError !== null}
            className="px-4 py-2 rounded bg-orange-500 hover:bg-orange-600 disabled:opacity-50 text-white font-medium transition-colors text-sm"
          >
            {loadingError === "returnClientSafe"
              ? "Calling..."
              : "Return ClientSafeError"}
          </button>
          <button
            type="button"
            onClick={handleThrowUnsafe}
            disabled={loadingError !== null}
            className="px-4 py-2 rounded bg-gray-700 hover:bg-gray-800 disabled:opacity-50 text-white font-medium transition-colors text-sm"
          >
            {loadingError === "throwUnsafe"
              ? "Calling..."
              : "Throw plain Error (sanitized)"}
          </button>
        </div>

        {errorResult && (
          <div className="mt-3 p-3 bg-white rounded border text-sm font-mono space-y-1">
            <div className="text-gray-500 text-xs font-sans mb-2">
              {errorResult.label}() &mdash;{" "}
              {errorResult.thrown ? (
                <span className="text-red-600 font-medium">
                  caught via try/catch
                </span>
              ) : (
                <span className="text-orange-600 font-medium">
                  received as return value
                </span>
              )}
            </div>
            <div>message: {errorResult.message}</div>
            {errorResult.statusCode !== undefined && (
              <div>statusCode: {errorResult.statusCode}</div>
            )}
            {errorResult.data !== undefined && (
              <div>data: {JSON.stringify(errorResult.data)}</div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
